import React, { useCallback, useState } from 'react';
import { ButtonV2, Flex } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { useWindowFunction } from '@shared/hooks/useWindowFunction';
import {
  AllyEyebrow,
  AllyPromoHeadline,
  AllyPromoSubtext,
  AllyAmountDisplay,
  AllyBenefitsCard,
  AllyBenefitList,
  AllyBenefitItem,
  AllyNudgeBar,
  AllyNudgeText,
  AllyPrimaryButton,
  AllySecondaryButton,
  AllyFinePrint,
} from './TransferToBankDialog.styles';
import type { TransferPartnerConfig } from './transferPartnerConfigs';

type AllyPromoStage = 'offer' | 'nudge' | 'opened';

export type AllyTransferPromoProps = Readonly<{
  config: TransferPartnerConfig;
  amountFormatted: string;
  /** No-bank state: triggers connect-bank flow. Pending state: dismisses promo and falls through to transfer confirm. */
  onSkip?: () => void;
  onBankPromoInteracted?: (cta: string, partner: string) => void;
}>;

export const AllyTransferPromo: React.FC<AllyTransferPromoProps> = ({ config, amountFormatted, onSkip, onBankPromoInteracted }) => {
  const { tq } = useTranslation('transferToBankDialog');
  const openWindow = useWindowFunction('open');
  const [stage, setStage] = useState<AllyPromoStage>('offer');

  const handleOpenAccount = useCallback(() => {
    onBankPromoInteracted?.('open_account', config.partnerId);
    openWindow(config.ctaUrl, '_blank', 'noopener,noreferrer');
    setStage('opened');
  }, [onBankPromoInteracted, openWindow, config.partnerId, config.ctaUrl]);

  const handleContinueWithTransfer = useCallback(() => {
    onBankPromoInteracted?.('continue_with_transfer', config.partnerId);
    setStage('nudge');
  }, [onBankPromoInteracted, config.partnerId]);

  const handleContinueAnyway = useCallback(() => {
    onBankPromoInteracted?.('continue_anyway', config.partnerId);
    onSkip?.();
  }, [onBankPromoInteracted, onSkip, config.partnerId]);

  const handleContinue = useCallback(() => {
    onBankPromoInteracted?.('bank_partner_continue', config.partnerId);
    onSkip?.();
  }, [onBankPromoInteracted, onSkip, config.partnerId]);

  const partnerOpened = stage === 'opened';

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      textAlign="center"
      paddingX={5}
      paddingBottom={6}
    >
      {!partnerOpened && (
        <>
          <AllyEyebrow marginBottom={4}>{tq`Transferring your funds`}</AllyEyebrow>
          <AllyPromoHeadline marginBottom={5}>{tq`Start your financial life together with Ally Bank.`}</AllyPromoHeadline>
          <AllyPromoSubtext
            marginBottom={7}
          >{tq`Combining your finances is a big step. We make it an easy one. Our Spending and Savings Accounts work better together, just like you two.`}</AllyPromoSubtext>
        </>
      )}

      <AllyAmountDisplay
        marginBottom={7}
        style={{ color: config.brandColor }}
      >
        {amountFormatted}
      </AllyAmountDisplay>

      {!partnerOpened && (
        <AllyBenefitsCard marginBottom={6}>
          <AllyBenefitList>
            <AllyBenefitItem>{tq`Smart savings tools`}</AllyBenefitItem>
            <AllyBenefitItem>{tq`Early direct deposit`}</AllyBenefitItem>
            <AllyBenefitItem>{tq`No hidden fees or minimums`}</AllyBenefitItem>
          </AllyBenefitList>
        </AllyBenefitsCard>
      )}

      {stage === 'nudge' ? (
        <AllyNudgeBar
          width="100%"
          marginBottom={6}
        >
          <AllyNudgeText>{tq`Your registry funds help celebrate today. Let Ally help you plan for tomorrow.`}</AllyNudgeText>
          <Flex
            flexDirection={['column', 'row']}
            gap={3}
          >
            <ButtonV2
              type="button"
              fullWidth
              flex={1}
              minWidth={0}
              size="md"
              shape="rounded"
              backgroundColor={config.brandColor}
              color="white"
              _hover={{ backgroundColor: config.brandColorHover }}
              onClick={handleOpenAccount}
            >
              {tq`Open Account`}
            </ButtonV2>
            <ButtonV2
              type="button"
              fullWidth
              flex={1}
              minWidth={0}
              size="md"
              shape="rounded"
              variant="outline"
              intent="neutral"
              onClick={handleContinueAnyway}
            >
              {tq`Continue anyway`}
            </ButtonV2>
          </Flex>
        </AllyNudgeBar>
      ) : (
        <Flex
          flexDirection="column"
          gap={3}
          width="100%"
          marginBottom={6}
        >
          <AllyPrimaryButton
            type="button"
            size="lg"
            shape="rounded"
            backgroundColor={config.brandColor}
            color="white"
            _hover={{ backgroundColor: config.brandColorHover }}
            onClick={partnerOpened ? handleContinue : handleOpenAccount}
          >
            {partnerOpened ? tq`Continue` : tq`Open Account`}
          </AllyPrimaryButton>
          {!partnerOpened && (
            <AllySecondaryButton
              type="button"
              size="lg"
              shape="rounded"
              variant="outline"
              intent="neutral"
              onClick={handleContinueWithTransfer}
            >
              {tq`Continue With Transfer`}
            </AllySecondaryButton>
          )}
        </Flex>
      )}

      <AllyFinePrint>{tq`Ally Bank, Member FDIC`}</AllyFinePrint>
    </Flex>
  );
};
